import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ThemeStylesSchema, type BuilderToEditorDto, type ElementTreeDto } from '@xshop/shared';
import type { ThemeStyles } from '../themes/registry';
import type { StorefrontThemePage } from '../themes/storefrontTheme';
import { useEditorShortcuts } from './editorShortcuts';
import { postToBuilder, useBuilderChannel } from './editorChannel';
import { useInlineEdit, type InlineRef } from './editorInline';
import { intentMessage, type EditorIntent } from './editorIntent';
import { useEditorKeys } from './editorKeys';
import { revealElement } from './editorElement';
import { isChrome, isThemeEditor, markAt, markFromId, NO_MARK, XRAY_CLASS, type EditorMark } from './editorVisit';
import { chromeZoom } from './chromeZoom';

/** The unsaved draft the builder is pushing down, and what is picked out of it right now. */
type EditorDraft = {
  /** The node of the element tree that is selected, at whatever depth the merchant clicked it. */
  selectedElement: string | null;
  /** The page as a tree of elements, as the builder is holding it; null until it has pushed one down. */
  tree: ElementTreeDto | null;
  /** The store's palette as the builder currently holds it, so repainting a token repaints the page. */
  styles: ThemeStyles | null;
  /** The store's stylesheet as the code editor is holding it, or null while it has not sent one. */
  customCss: string | null;
  /** What the builder calls the sections and blocks its theme names, which the toolbar and tag name them by. */
  labels: Readonly<Record<string, string>>;
};

/** How the builder has asked for this page to be looked at, and how large its chrome is drawn; none touches the draft. */
export type EditorView = { preview: boolean; xray: boolean; zoom: number };

/** What a builder preview holds beyond an ordinary visit: the draft, the selection, and the way back up. */
export type EditorSession = EditorDraft & {
  active: boolean;
  /** How the page is being looked at, which is what decides whether the chrome is drawn at all. */
  view: EditorView;
  /** Asks the builder for a change to the selection; the canvas performs none of them itself. */
  sendIntent: (intent: EditorIntent) => void;
};

const EMPTY: EditorDraft = { selectedElement: null, tree: null, styles: null, customCss: null, labels: {} };

/** What a preview draws before the builder has said otherwise: itself, with every mark the builder has. */
const PLAIN_VIEW: EditorView = { preview: false, xray: false, zoom: 1 };

// Reads the palette off an apply as a patch of its own: an absent or refused one contributes no key, so
// the page keeps the palette already on screen. A page that cannot be repainted must still be a page.
const stylePatch = (sent: unknown): Pick<EditorDraft, 'styles'> | Record<string, never> => {
  if (sent === undefined) return {};
  const parsed = ThemeStylesSchema.safeParse(sent);
  return parsed.success ? { styles: parsed.data as ThemeStyles } : {};
};
const INERT: EditorSession = { ...EMPTY, active: false, view: PLAIN_VIEW, sendIntent: () => undefined };

// Scrolls to what the builder's tree just selected, a frame later: a node added a message ago is not in
// the page until React has drawn the tree that carries it, and a scroll to nothing scrolls nowhere.
const reveal = ({ elementId }: EditorMark): void => {
  if (elementId) window.requestAnimationFrame(() => revealElement(elementId));
};

// Turns one resolved mark into the draft keys it sets, so a click and a `select` land in the same state.
const picked = (mark: EditorMark): Pick<EditorDraft, 'selectedElement'> => ({ selectedElement: mark.elementId });

// Applies what the builder says, leaving every other message it sends to the module that answers it.
function useBuilderMessages(
  active: boolean,
  apply: (draft: Partial<EditorDraft>) => void,
  onView: (view: EditorView) => void
): void {
  const onMessage = useCallback((message: BuilderToEditorDto) => {
    // Narrowed by the same reader the saved edits go through, so a draft and a saved edit are one type.
    // Only the parts an apply carries are replaced: the code editor sends the one file it is editing.
    if (message.type === 'apply') {
      return apply({
        ...(message.tree ? { tree: message.tree } : {}),
        ...stylePatch(message.styles),
        ...(message.customCss !== undefined ? { customCss: message.customCss } : {}),
        ...(message.labels ? { labels: message.labels } : {})
      });
    }
    if (message.type === 'view') return onView({ preview: message.preview, xray: message.xray, zoom: chromeZoom(message.scale) });
    if (message.type !== 'select') return;
    const mark = markFromId(message.elementId);
    apply(picked(mark));
    if (message.reveal) reveal(mark);
  }, [apply, onView]);

  useBuilderChannel(active, onMessage);
}

// Reports whether a click landed on the builder's own chrome, or inside the text a merchant is typing into.
const isEditorFurniture = (target: EventTarget | null, editing: InlineRef): boolean => {
  if (isChrome(target)) return true;
  const host = editing.current?.element;
  return Boolean(host && target instanceof Node && host.contains(target));
};

/**
 * Turns a click anywhere in the preview into a selection instead of the thing it would normally do:
 * inside the builder a button is a node to edit, not an action to take. The innermost node under the
 * pointer is what is resolved, so clicking a headline selects the headline and not the box around it.
 */
function useSelectOnClick(active: boolean, select: (mark: EditorMark) => void, editing: InlineRef): void {
  useEffect(() => {
    if (!active) return undefined;
    const onClick = (event: MouseEvent) => {
      if (isEditorFurniture(event.target, editing)) return;
      event.preventDefault();
      event.stopPropagation();
      const mark = markAt(event.target);
      select(mark);
      postToBuilder({ type: 'clicked', elementId: mark.elementId });
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [active, select, editing]);
}

// Marks the whole document while x-ray is on, which is the one hook the outline-everything rule hangs off.
function useXrayClass(on: boolean): void {
  useEffect(() => {
    document.documentElement.classList.toggle(XRAY_CLASS, on);
    return () => document.documentElement.classList.remove(XRAY_CLASS);
  }, [on]);
}

// Holds the builder's side of one preview: the draft it is pushing down, and what it has open in it.
export function useEditorSession(config: StorefrontThemePage | null): EditorSession {
  const active = useMemo(() => isThemeEditor(), []);
  const [state, setState] = useState<EditorDraft>(EMPTY);
  const [view, setView] = useState<EditorView>(PLAIN_VIEW);
  // Undo, redo and save, forwarded from inside the frame — the builder's own listener cannot hear them.
  useEditorShortcuts(active);

  const apply = useCallback((next: Partial<EditorDraft>) => setState((current) => ({ ...current, ...next })), []);
  const select = useCallback((mark: EditorMark) => apply(picked(mark)), [apply]);
  const onTyped = useCallback((ownerId: string, path: string, value: string) => {
    postToBuilder({ type: 'typed', elementId: ownerId, propPath: path, value });
  }, []);
  const sendIntent = useCallback((intent: EditorIntent) => {
    const message = intentMessage(intent);
    if (!message) return;
    // Rising a level moves the selection here and now; every other intent is the builder's to perform,
    // and the selection follows whatever it sends back rather than guessing where the node has gone.
    if (message.type === 'clicked') apply(picked(markFromId(message.elementId)));
    postToBuilder(message);
  }, [apply]);

  // Read by the key handler at the moment a key is pressed, so Escape and Delete never rebind a
  // listener per keystroke and never act on a selection the merchant has already moved off.
  const selection = useRef<EditorMark>(NO_MARK);
  selection.current = { elementId: state.selectedElement };
  const deselect = useCallback(() => {
    apply(picked(NO_MARK));
    postToBuilder({ type: 'clicked', elementId: null });
  }, [apply]);

  // Preview hands the page back to the pointer: a click is a link again, and typing is the page's own.
  // The channel stays open on `active` alone, since the message that ends preview arrives down it.
  const live = active && !view.preview;
  const editing = useInlineEdit(live, onTyped);
  useBuilderMessages(active, apply, setView);
  useXrayClass(live && view.xray);
  useSelectOnClick(live, select, editing);
  useEditorKeys(live, { selection, deselect, sendIntent });

  // Announced on every page the preview lands on, so the builder's tree follows a preview that navigated.
  useEffect(() => {
    if (!active || !config) return;
    postToBuilder({ type: 'ready', slug: config.slug, page: config.page });
  }, [active, config]);

  return active ? { active, ...state, view, sendIntent } : INERT;
}
